import { doc, getDoc } from "firebase/firestore";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useUserContext } from "../../../context/UserProvider";
import { db } from "../../../firebase";
import CreateImage from "../../UserPage/DashboardPage/CreateImage";
import { getDashboardId } from "../ConfirmationUI";
import InviteActions from "./InviteActions";
import { buildInviteUrl, formatUpcomingStart } from "./inviteLink";
import ManualTrackingOverlay from "./ManualTrackingOverlay";
import { countRaceReferrals, getRaceReadyProgress, getRaceReadySteps, isDeviceConnected } from "./raceReadySteps";
import { saveRaceReady, trackRaceReady } from "./raceReadyStore";
import { countAllReferrals, referralGoalText } from "./referralRewards";
import "./RaceReady.css";

const CONFETTI_COLORS = ["#0a6cc4", "#4facfe", "#ff851f", "#22c55e", "#facc15"];

function Confetti() {
  return (
    <div className="rr-confetti" aria-hidden="true">
      {Array.from({ length: 28 }, (_, index) => (
        <span
          key={index}
          style={{
            left: `${(index * 37) % 100}%`,
            background: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
            animationDelay: `${(index % 7) * 0.06}s`,
            transform: `rotate(${index * 29}deg)`,
          }}
        />
      ))}
    </div>
  );
}

function StepCard({ number, step, isNext, title, text, children }) {
  return (
    <div className={`rr-step rr-step-${step.id} ${step.optional ? "rr-step-bonus" : ""} ${step.done ? "is-done" : ""} ${isNext ? "is-next" : ""}`}>
      <div className="rr-step-marker" aria-hidden="true">
        {step.done ? "✓" : step.optional ? "🎁" : number}
      </div>
      <div className="rr-step-body">
        <div className="rr-step-heading">
          <h3 className="rr-step-title">{title}</h3>
          {step.optional && <span className="rr-tag">Bonus</span>}
          {isNext && <span className="rr-tag rr-tag-next">Next</span>}
        </div>
        <p className="rr-step-text">{text}</p>
      </div>
      {children && <div className="rr-step-actions">{children}</div>}
    </div>
  );
}

// "Get challenge-ready" checklist at the top of every confirmation page, and on the Home page (place="home").
export default function RaceReadyChecklist({ selectedRace, raceData, raceName, imagePreset, inviteTheme, previewMode = false, place = "confirmation", showHeader = true, onPosted }) {
  const { user, userData, userDetails, linkedDevice } = useUserContext();
  const [previewProgress, setPreviewProgress] = useState({});
  const [referralCount, setReferralCount] = useState(0);
  // Lifetime referrals (all challenges), used for the reward goal line on the invite step.
  const [allReferrals, setAllReferrals] = useState(0);
  const [showPicture, setShowPicture] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const wasComplete = useRef(null);

  const legId = selectedRace?.legId;
  const raceReady = previewMode ? previewProgress : selectedRace?.raceReady || {};
  const bibLink = raceData?.metadata?.bib_link;
  const hasBib = Boolean(bibLink && bibLink !== "#") || previewMode;
  const bib = previewMode ? "1842" : `${selectedRace?.bibNumber ?? ""}`.trim();
  const referralCode = userData?.referral_link || "";
  const inviteUrl = buildInviteUrl(window.location.origin, raceName, referralCode, inviteTheme?.promoCode);

  const startDay = formatUpcomingStart(selectedRace?.raceInfo);
  const steps = getRaceReadySteps({ raceReady, linkedDevice, referralCount, hasBib });
  const progress = getRaceReadyProgress(steps);
  const nextStepId = steps.find((step) => !step.optional && !step.done)?.id;

  // Previews keep progress in memory so the page can be tried without touching real data.
  const save = (changes) => {
    if (previewMode) {
      setPreviewProgress((current) => ({ ...current, ...Object.fromEntries(Object.entries(changes).map(([key, value]) => [key, value === "now" ? Date.now() : value])) }));
      return;
    }
    saveRaceReady(user?.uid, legId, changes);
  };
  const track = (eventName, params = {}) => {
    if (!previewMode) trackRaceReady(eventName, { race: raceName, place, ...params });
  };

  useEffect(() => {
    if (previewMode || !user?.uid || !legId) return;
    // seenAt means the member saw the checklist on the confirmation page.
    if (place === "confirmation" && !selectedRace?.raceReady?.seenAt) saveRaceReady(user.uid, legId, { seenAt: "now" });
    track("race_ready_viewed", { done: progress.done, total: progress.total });
    getDoc(doc(db, "referrals", user.uid))
      .then((snapshot) => {
        setReferralCount(countRaceReferrals(snapshot.data(), selectedRace?.raceInfo?.name));
        setAllReferrals(countAllReferrals(snapshot.data()));
      })
      .catch(() => {});
    // Runs once per visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, legId]);

  // Celebrate only when the last step gets done on this visit, not when the page opens already complete.
  useEffect(() => {
    if (wasComplete.current === false && progress.complete) {
      setCelebrate(true);
      track("race_ready_complete");
    }
    wasComplete.current = progress.complete;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress.complete]);

  const stepDone = (id, changes, action) => {
    save(changes);
    track("race_ready_step", { step: id, action });
  };

  // Only the required steps are numbered; the bonus step sits under them without a number.
  const requiredSteps = steps.filter((step) => !step.optional);
  const stepNumber = (id) => requiredSteps.findIndex((step) => step.id === id) + 1;

  // Saved for the reminder emails: the member's invite link and the challenge's invite goal.
  useEffect(() => {
    if (previewMode || !user?.uid || !legId) return;
    const saved = selectedRace?.raceReady || {};
    const inviteGoal = inviteTheme?.inviteGoal || "";
    if (saved.inviteUrl === inviteUrl && (saved.inviteGoal || "") === inviteGoal) return;
    saveRaceReady(user.uid, legId, { inviteUrl, inviteGoal: inviteGoal || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, legId, inviteUrl]);

  const stepById = Object.fromEntries(steps.map((step) => [step.id, step]));
  const percent = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <section className="rr-checklist" aria-label="Get challenge-ready">
      {showHeader && (
        <div className={`rr-checklist-head ${progress.complete ? "is-complete" : ""}`}>
          {celebrate && <Confetti />}
          {/* When everything is done the "You're all set" card below says so; the header just shows the full bar. */}
          <h2 id="rr-checklist-title" className="rr-checklist-title">
            Get challenge-ready
          </h2>
          <p className="rr-checklist-sub">
            {progress.done} of {progress.total} steps done
          </p>
          <div className="rr-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
            <span style={{ width: `${percent}%` }} />
          </div>
        </div>
      )}

      {progress.complete && (
        <div className="rr-all-set" role="status">
          <span className="rr-all-set-icon" aria-hidden="true">
            ✓
          </span>
          <div>
            <p className="rr-all-set-title">
              You're all set{userDetails?.firstName ? `, ${userDetails.firstName}` : ""}!
            </p>
            <p className="rr-all-set-text">
              {startDay ? `Your km start counting on ${startDay}. ` : "Your km are counting. "}
              {stepById.invite.done ? "See you on the leaderboard!" : "Want company? Invite a friend below."}
            </p>
          </div>
        </div>
      )}

      <div className="rr-steps" style={{ "--rr-cols": requiredSteps.length }}>
        <StepCard
          number={stepNumber("device")}
          step={stepById.device}
          isNext={nextStepId === "device"}
          title="Connect your watch or app"
          text={
            isDeviceConnected(linkedDevice)
              ? `Connected to ${linkedDevice}. Your km will post on their own.`
              : stepById.device.done
              ? "You're logging your km yourself."
              : "Your km post on their own, so every step counts."
          }
        >
          {isDeviceConnected(linkedDevice) ? null : stepById.device.done ? (
            <button type="button" className="rr-btn rr-btn-link" onClick={() => setShowManual(true)}>
              How to log km
            </button>
          ) : (
            <>
              <Link to="/account" target="_blank" rel="noopener noreferrer" className="rr-btn rr-btn-primary" onClick={() => track("race_ready_step", { step: "device", action: "connect" })}>
                Connect
              </Link>
              <button
                type="button"
                className="rr-btn rr-btn-secondary"
                onClick={() => {
                  stepDone("device", { device: "manual" }, "manual");
                  setShowManual(true);
                }}
              >
                I'll track manually
              </button>
            </>
          )}
        </StepCard>

        {stepById.picture && (
          <StepCard
            number={stepNumber("picture")}
            step={stepById.picture}
            isNext={nextStepId === "picture"}
            title="Make your challenge picture"
            text={stepById.picture.done ? "Picture made. Looking good!" : "Add your photo to the official frame and show everyone you're in."}
          >
            <button type="button" className={`rr-btn ${stepById.picture.done ? "rr-btn-secondary" : "rr-btn-primary"}`} onClick={() => setShowPicture(true)}>
              {stepById.picture.done ? "Make another" : "Create picture"}
            </button>
          </StepCard>
        )}

        {stepById.bib && (
          <StepCard
            number={stepNumber("bib")}
            step={stepById.bib}
            isNext={nextStepId === "bib"}
            title="Download your BIB"
            text={stepById.bib.done ? `BIB #${bib} downloaded.` : `Print BIB #${bib} and wear it in your challenge photos.`}
          >
            <a
              href={bibLink || "#"}
              target="_blank"
              rel="noreferrer"
              className={`rr-btn ${stepById.bib.done ? "rr-btn-secondary" : "rr-btn-primary"}`}
              onClick={() => stepDone("bib", { bibAt: "now" }, "download")}
            >
              {stepById.bib.done ? "Download again" : "Download BIB"}
            </a>
          </StepCard>
        )}

        <StepCard
          number={stepNumber("dashboard")}
          step={stepById.dashboard}
          isNext={nextStepId === "dashboard"}
          title="Open your dashboard"
          text={stepById.dashboard.done ? "You know where to find your km and the leaderboard." : "See your km, your streak and the leaderboard."}
        >
          <Link
            to={`/dashboard/${getDashboardId(selectedRace)}`}
            className={`rr-btn ${stepById.dashboard.done ? "rr-btn-secondary" : "rr-btn-primary"}`}
            onClick={() => stepDone("dashboard", { dashboardAt: "now" }, "open")}
          >
            Go to dashboard
          </Link>
        </StepCard>
      </div>

      <StepCard
        step={stepById.invite}
        title="Invite a friend"
        text={
          stepById.invite.done
            ? `${referralCount} friend${referralCount === 1 ? "" : "s"} joined with your link. Thank you!`
            : inviteTheme
            ? `${inviteTheme.inviteText} ${allReferrals > 0 ? referralGoalText(allReferrals) : inviteTheme.inviteGoal || ""}`.trim()
            : "Share the challenge and earn referral rewards."
        }
      >
        <InviteActions
          text={inviteTheme ? inviteTheme.shareText(Boolean(referralCode || inviteTheme.promoCode)) : `Join me in ${selectedRace?.raceInfo?.name || "this challenge"}:`}
          url={inviteUrl}
          subject={inviteTheme?.shareSubject || `Join me in ${selectedRace?.raceInfo?.name || "this challenge"}`}
          onShared={(method) => track("race_invite_shared", { place: place === "home" ? "home_checklist" : "checklist", method, referral_link: referralCode })}
        />
      </StepCard>

      {showPicture && (
        <div
          className="rr-picture-overlay"
          onClick={(event) => {
            if (event.target.hasAttribute?.("data-close")) setShowPicture(false);
          }}
        >
          <CreateImage
            raceName={selectedRace?.raceInfo?.name}
            imagePreset={imagePreset}
            distance={selectedRace?.raceInfo?.distance}
            onDone={(method) => {
              stepDone("picture", { pictureAt: "now" }, method);
              // Post sends the picture to the feed; close the picture pop-up (and, on Home, the checklist) so the feed shows.
              if (method === "post") {
                setShowPicture(false);
                onPosted?.();
              }
            }}
          />
        </div>
      )}
      {showManual && <ManualTrackingOverlay onClose={() => setShowManual(false)} />}
    </section>
  );
}
